import { useState } from 'react';
import {
  Flow,
  FLOW_LABELS,
  LAYOUTS,
  LAYOUT_LABELS,
  Layout,
  EmailDraft,
  Settings,
  RECOMMENDATION_SOURCES,
  RECOMMENDATION_SOURCE_LABELS,
  RecommendationSource,
} from './types';
import generateEmailHtml, { generateProductBlockHtml } from './generateEmailHtml';
import useCopyToClipboard from './useCopyToClipboard';

const EMPTY_DRAFT: EmailDraft = {
  subject: '',
  previewText: '',
  headline: '',
  body: '',
};

type RegenerableField = 'subject' | 'body';

type EmailEditorProps = {
  flow: Flow;
  index: number;
  draft: EmailDraft | null;
  layout: Layout;
  settings: Settings;
  onChange: (draft: EmailDraft) => void;
  onLayoutChange: (layout: Layout) => void;
  onSettingsChange: (settings: Settings) => void;
  onClose: () => void;
  onRegenerate?: () => Promise<void>;
  onRegenerateField?: (field: RegenerableField) => Promise<void>;
};

function EmailEditor({
  flow,
  index,
  draft,
  layout,
  settings,
  onChange,
  onLayoutChange,
  onSettingsChange,
  onClose,
  onRegenerate,
  onRegenerateField,
}: EmailEditorProps) {
  const current = draft ?? EMPTY_DRAFT;
  const fullHtml = useCopyToClipboard();
  const productsBlock = useCopyToClipboard();

  let copyHtmlLabel = 'Copy entire email';
  if (fullHtml.copied) {
    copyHtmlLabel = 'Copied!';
  } else if (fullHtml.error) {
    copyHtmlLabel = 'Copy failed';
  }

  let copyBlockLabel = 'Copy products only';
  if (productsBlock.copied) {
    copyBlockLabel = 'Copied!';
  } else if (productsBlock.error) {
    copyBlockLabel = 'Copy failed';
  }
  const [regenerating, setRegenerating] = useState<'all' | RegenerableField | null>(null);

  const handleRegenerate = async () => {
    if (!onRegenerate) return;
    setRegenerating('all');
    try {
      await onRegenerate();
    } finally {
      setRegenerating(null);
    }
  };

  const handleRegenerateField = async (field: RegenerableField) => {
    if (!onRegenerateField) return;
    setRegenerating(field);
    try {
      await onRegenerateField(field);
    } finally {
      setRegenerating(null);
    }
  };

  return (
    <div className="eg-editor">
      <div className="eg-editor-header">
        <button type="button" className="eg-editor-back" onClick={onClose}>
          ← All emails
        </button>
        <span className="eg-editor-title">
          {`${FLOW_LABELS[flow]} — Email ${index + 1}`}
        </span>
        <button
          type="button"
          className="eg-btn-primary"
          onClick={handleRegenerate}
          disabled={!onRegenerate || regenerating !== null}
        >
          {regenerating === 'all' ? 'Regenerating…' : 'Regenerate'}
        </button>
      </div>
      <div className="eg-editor-fields">
        <label htmlFor="eg-subject" className="eg-field">
          <div className="eg-field-label-row">
            <span className="eg-field-label">Subject</span>
            {onRegenerateField && (
              <button
                type="button"
                className="eg-regenerate-field"
                onClick={() => handleRegenerateField('subject')}
                disabled={regenerating !== null}
              >
                {regenerating === 'subject' ? 'Regenerating…' : 'Regenerate Subject'}
              </button>
            )}
          </div>
          <input
            id="eg-subject"
            type="text"
            value={current.subject}
            onChange={(e) => onChange({ ...current, subject: e.target.value })}
            placeholder="Enter subject line"
          />
        </label>
        <label htmlFor="eg-preview-text" className="eg-field">
          <span className="eg-field-label">Preview text</span>
          <input
            id="eg-preview-text"
            type="text"
            value={current.previewText}
            onChange={(e) => onChange({ ...current, previewText: e.target.value })}
            placeholder="Enter preview text"
          />
        </label>
        <label htmlFor="eg-headline" className="eg-field">
          <span className="eg-field-label">Headline</span>
          <input
            id="eg-headline"
            type="text"
            value={current.headline}
            onChange={(e) => onChange({ ...current, headline: e.target.value })}
            placeholder="Enter headline"
          />
        </label>
        <label htmlFor="eg-body" className="eg-field">
          <div className="eg-field-label-row">
            <span className="eg-field-label">Body</span>
            {onRegenerateField && (
              <button
                type="button"
                className="eg-regenerate-field"
                onClick={() => handleRegenerateField('body')}
                disabled={regenerating !== null}
              >
                {regenerating === 'body' ? 'Regenerating…' : 'Regenerate Body'}
              </button>
            )}
          </div>
          <textarea
            id="eg-body"
            value={current.body}
            onChange={(e) => onChange({ ...current, body: e.target.value })}
            placeholder="Enter body copy"
            rows={8}
          />
        </label>
        <label htmlFor="eg-layout" className="eg-field">
          <span className="eg-field-label">Product layout</span>
          <select
            id="eg-layout"
            value={layout}
            onChange={(e) => onLayoutChange(e.target.value as Layout)}
          >
            {LAYOUTS.map((l) => (
              <option key={l} value={l}>{LAYOUT_LABELS[l]}</option>
            ))}
          </select>
        </label>
        {flow === 'cross-sell' && (
          <label htmlFor="eg-recommendation-source" className="eg-field">
            <span className="eg-field-label">Recommendation source</span>
            <select
              id="eg-recommendation-source"
              value={settings.crossSellRecommendationSource}
              onChange={(e) => onSettingsChange({
                ...settings,
                crossSellRecommendationSource: e.target.value as RecommendationSource,
              })}
            >
              {RECOMMENDATION_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {RECOMMENDATION_SOURCE_LABELS[source]}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="eg-editor-actions">
          <button
            type="button"
            className="eg-btn-primary"
            onClick={() => fullHtml.copy(generateEmailHtml(current, layout, settings, flow))}
          >
            {copyHtmlLabel}
          </button>
          <button
            type="button"
            className="eg-btn-secondary"
            onClick={() => productsBlock.copy(generateProductBlockHtml(layout, settings, flow))}
          >
            {copyBlockLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailEditor;
